import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Reveal, PageHero } from './LandingShared';
import { useTranslation } from '../contexts/TranslationContext';
import { PhoneIcon, EnvelopeIcon, MapPinIcon, BuildingOffice2Icon, UserIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';

/**
 * Contact Component
 * Displays contact information, location details, and an embedded map.
 * Styled with Tailwind CSS for a modern, interactive, and professional layout.
 */
const Contact = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Helmet>
        <title>Contact Us — Cogent Platform</title>
        <meta name="description" content="Get in touch with Cogent Asset Management Ltd. Find our office location, phone numbers, and email addresses. Book a consultation with our team." />
        <link rel="canonical" href="https://cogentplatform.com/contact" />
      </Helmet>
      <Navbar />

      <main className="flex-grow">
        {/* Hero Banner */}
        <PageHero title={t('contact.title')} />

        {/* Content Section */}
        <div className="bg-gradient-to-b from-white to-slate-50 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Left Side: Contact Details */}
              <div className="space-y-8">
                {/* Our Contact Card */}
                <Reveal delay={0}>
                  <div className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
                    <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
                    <div className="p-6 sm:p-8">
                      <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md">
                          <BuildingOffice2Icon className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="ml-4 text-2xl font-bold text-gray-900">
                          {t('contact.ourContact')}
                        </h2>
                      </div>
                      <ul className="space-y-4 text-lg text-gray-600">
                        <li className="flex items-center">
                          <PhoneIcon className="h-6 w-6 mr-3 text-blue-500" />
                          <span>Malaysia +60 87410750</span>
                        </li>
                        <li className="flex items-center">
                          <EnvelopeIcon className="h-6 w-6 mr-3 text-blue-500" />
                          <a href="mailto:operations@cogentplatform.com" className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                            operations@cogentplatform.com
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Reveal>

                {/* Client Feedback Card */}
                <Reveal delay={150}>
                  <div className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden">
                    <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
                    <div className="p-6 sm:p-8">
                      <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md">
                          <UserIcon className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="ml-4 text-2xl font-bold text-gray-900">
                          {t('contact.clientFeedback')}
                        </h2>
                      </div>
                      <ul className="space-y-4 text-lg text-gray-600">
                        <li className="flex items-center">
                          <UserIcon className="h-6 w-6 mr-3 text-blue-500" />
                          <span>{t('contact.ceoOffice')}</span>
                        </li>
                        <li className="flex items-center">
                          <EnvelopeIcon className="h-6 w-6 mr-3 text-blue-500" />
                          <a href="mailto:ian.lancaster@cogentplatform.com" className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                            ian.lancaster@cogentplatform.com
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Reveal>
              </div>

              {/* Right Side: Location and Map */}
              <Reveal delay={300}>
                <div className="rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden h-full">
                  <div className="h-1 bg-gradient-to-r from-blue-600 to-cyan-500" />
                  <div className="p-6 sm:p-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shadow-md">
                        <MapPinIcon className="h-6 w-6 text-white" />
                      </div>
                      <h2 className="ml-4 text-2xl font-bold text-gray-900">
                        {t('contact.ourLocation')}
                      </h2>
                    </div>
                    <address className="text-lg text-gray-600 not-italic mb-6">
                      Level 3 No. 25,<br />
                      Labuan Times Square,<br />
                      87000 Labuan F.T<br />
                      Malaysia
                    </address>
                    <div className="rounded-xl overflow-hidden">
                      <iframe
                        title="Labuan Times Square Location"
                        src="https://maps.google.com/maps?q=labuan%20time%20square&t=m&z=17&output=embed&iwloc=near"
                        width="100%"
                        height="350"
                        style={{ border: '0' }}
                        allowFullScreen=""
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </Reveal>

            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
